import React, { useState, useRef, ChangeEvent } from 'react'

import Button from './Button'
import DocumentUploadIcon from '../../../assets/icons/document-upload.svg?react'
import EyeIcon from '../../../assets/icons/eye.svg?react'
import { useI18N } from '../../../libraries/i18n/hook'
import ErrorMessage from '../errors/ErrorMessage'
import Translate from '../../../libraries/i18n/Translate'

interface FileUploadProps {
  onFileSelected: (file: File) => void
  maxMbSize: number // max file size in MB
  accept?: string
  label?: string
  disabled?: boolean
  hideFileName?: boolean
  buttonClassName?: string
  buttonVariant?: 'primary' | 'secondary' | 'tertiary' | 'flat' | 'feedback'
  hideBorder?: boolean
  defaultFile?: { label: string, url?: string } // file to be displayed by default
  defaultButtonLabel?: { select: string, change: string }
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelected,
  accept = '.pdf,.doc,.docx',
  label = 'Upload File',
  disabled = false,
  hideFileName = false,
  buttonVariant = 'primary',
  hideBorder = false,
  defaultButtonLabel,
  defaultFile,
  maxMbSize,
}) => {
  const { t } = useI18N()
  const [error, setError] = useState<string | React.ReactNode | undefined>(undefined)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setError(null)
    const files = e.target.files
    if (files && files.length > 0) {
      const file = files[0]
      // Check file size (max 5MB)
      if (file.size > maxMbSize * 1024 * 1024) {
        setError(<Translate i18nKey='common.file.exceeds.size' values={{ size: `${maxMbSize}MB` }} />)
        setSelectedFileUrl(null)
        return
      }

      setSelectedFile(file)
      onFileSelected(file)
      const url = URL.createObjectURL(file)
      setSelectedFileUrl(url)
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className='w-full'>
      {label && (
        <label className='block text-xs md:text-sm font-medium text-gray-700 mb-1'>{label}</label>
      )}
      <div className={`${hideBorder ? 'bg-white' : 'ring ring-1 ring-gray-200'} p-1 flex flex-nowrap items-center rounded-full w-full` }>
        <Button
          icon={<DocumentUploadIcon className='hidden md:inline-flex' />}
          type='button'
          variant={buttonVariant}
          onClick={handleButtonClick}
          disabled={disabled}
          size='sm'
          className='min-w-[105px] min-h-[35px] lg:min-h-auto px-3 box-content lg:box-border whitespace-nowrap'
        >
          {
            selectedFileUrl || defaultFile?.url
            ? defaultButtonLabel?.change
            : defaultButtonLabel?.select
          }
        </Button>
        {!hideFileName && (
          <span className='ml-3 text-3xs sm:text-xs md:text-sm text-gray-500 md:w-auto truncate'>
            {selectedFile ? selectedFile.name :
              defaultFile?.url ? defaultFile?.label
              : t('common.file.noSelected')}
          </span>
        )}
        <div className='grow' />
        {(selectedFileUrl || defaultFile?.url) && (
          <a href={selectedFileUrl ?? defaultFile?.url} target='_blank' rel='noreferrer'>
            <Button variant='primary' type='button' size='sm' outlined iconOnlyBelow='md' icon={<EyeIcon className='stroke-deep-green' />}>
              {t('common.see')}
            </Button>
          </a>
        )}
      </div>
      <input
        type='file'
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={accept}
        className='hidden'
        disabled={disabled}
      />
      {error && <ErrorMessage message={error} />}
    </div>
  )
}

export default FileUpload
